import { FC, useContext, useEffect, useRef, useState } from "react";
import api from "api/landlord/applications";
import { Loader } from "shared/components/Loader";

import { ApplicantSettings, ApplicantType } from "shared/types/application";
import { ReportHeader } from "../../components/ReportHeader";
import {
  SelfAttestedIncome,
  VerificationReport,
  VerifiedIncomeReport
} from "types/landlord/reports";
import { VerificationNames, VerificationStatus } from "shared/types";
import { useQueries } from "@tanstack/react-query";
import { getVerificationQueryKeys } from "../../utils/getVerificationQueryKeys";
import { VerifiedIncomeSummary } from "views/applications-id-report/slices/IncomeReportSlice/slices/VerifiedIncomeSummary";
import { DocumentWarnings } from "views/applications-id-report/slices/IncomeReportSlice/slices/DocumentWarnings";
import { VerifiedIncomeSources } from "views/applications-id-report/slices/IncomeReportSlice/slices/VerifiedIncomeSources";
import { SelfReportedIncomeSources } from "views/applications-id-report/slices/IncomeReportSlice/slices/SelfReportedIncomeSources";
import { SelfReportedIncomeSummary } from "views/applications-id-report/slices/IncomeReportSlice/slices/SelfReportedIncomeSummary";
import { VerifiedNetEarnings } from "views/applications-id-report/slices/IncomeReportSlice/slices/VerifiedNetEarnings";
import { ProjectedDebtIncomeRatio } from "views/applications-id-report/slices/IncomeReportSlice/slices/ProjectedDebtincomeRatio";
import { EditIncomeModal } from "./components/EditIncomeModal";
import { HousingAssistance } from "views/applications-id-report/slices/IncomeReportSlice/slices/HousingAssistance";
import { Show } from "shared/components";
import { getFullName } from "shared/utils/get-full-name";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { Template } from "types/landlord/verificationTemplate";
import {
  PrintAllContext,
  PrintReady
} from "views/applications-id-report/providers/PrintProvider";
import { Applicant } from "types/application";

interface IncomeReportSliceProps {
  applicant: Applicant;
  screeningCriteria?: ApplicantSettings;
  templateRecommendationSettings?: string;
  verificationTemplate?: Template;
}

export const IncomeReportSlice: FC<IncomeReportSliceProps> = ({
  applicant,
  templateRecommendationSettings,
  verificationTemplate
}) => {
  const [showEditIncomeModal, setShowEditIncomeModal] =
    useState<boolean>(false);

  const { documentPrintingState, setDocumentPrintingState } =
    useContext(PrintAllContext);
  const housingAssistanceScroll = useRef<HTMLDivElement>(null);
  const offerLetterScroll = useRef<HTMLDivElement>(null);

  const selfAttestedVerification = applicant.verifications.find(
    (verification) => verification.name === VerificationNames.IncomeVerification
  );

  const verifiedIncomeVerification = applicant.verifications.find(
    (verification) =>
      verification.name === VerificationNames.VerifiedIncomeVerification
  );

  const executeScrollToHA = () =>
    housingAssistanceScroll.current?.scrollIntoView();

  const executeScrollToOfferLetter = () =>
    offerLetterScroll.current?.scrollIntoView();

  const [selfAttestedIncomeMeta, verifiedIncomeMeta] = useQueries({
    queries: [
      {
        enabled: !!selfAttestedVerification?.id,
        queryKey: getVerificationQueryKeys({
          reportName: VerificationNames.IncomeVerification,
          applicationId: applicant.application_id,
          applicantId: applicant.id,
          verificationId: selfAttestedVerification?.id
        }),
        queryFn: () => {
          if (selfAttestedVerification)
            return api
              .getApplicantVerification(
                applicant.application_id,
                applicant.id,
                selfAttestedVerification.id
              )
              .then(
                (data) =>
                  data.verification as VerificationReport<SelfAttestedIncome>
              );
          else return null;
        }
      },
      {
        enabled: !!verifiedIncomeVerification?.id,
        queryKey: getVerificationQueryKeys({
          reportName: VerificationNames.VerifiedIncomeVerification,
          applicationId: applicant.application_id,
          applicantId: applicant.id,
          verificationId: verifiedIncomeVerification?.id
        }),
        queryFn: () => {
          if (verifiedIncomeVerification)
            return api
              .getApplicantVerification(
                applicant.application_id,
                applicant.id,
                verifiedIncomeVerification.id
              )
              .then(
                (data) =>
                  data.verification as VerificationReport<VerifiedIncomeReport>
              );
          else return null;
        }
      }
    ]
  });
  useEffect(() => {
    if (selfAttestedIncomeMeta.data) {
      const printingState = documentPrintingState?.find(
        (x) => x.reportId == selfAttestedIncomeMeta.data?.id
      );
      if (printingState?.status !== "ready") {
        const numDocs =
          selfAttestedIncomeMeta.data.report.income_sources.reduce(
            (acc, item) => {
              const incomeSource =
                item[
                  item.income_source.toLowerCase().replaceAll(/[ -]+/g, "_")
                ];
              if (incomeSource.verification_method?.bank_statement_files)
                acc +=
                  incomeSource.verification_method.bank_statement_files.filter(
                    (file) => file && file.asset?.extension === "pdf"
                  ).length || 0;

              if (incomeSource.verification_method?.other_files)
                acc +=
                  incomeSource.verification_method?.other_files.filter(
                    (file) => file && file.asset?.extension === "pdf"
                  ).length || 0;

              if (incomeSource.verification_method?.pay_stub_files)
                acc +=
                  incomeSource.verification_method?.pay_stub_files.filter(
                    (file) => file && file.asset?.extension === "pdf"
                  ).length || 0;

              if (
                incomeSource.offer_letter_file &&
                incomeSource.offer_letter_file.asset?.extension === "pdf"
              )
                acc += 1;

              if (incomeSource.voucher_file?.length)
                acc +=
                  incomeSource.voucher_file.filter(
                    (file) => file && file.asset?.extension === "pdf"
                  ).length || 0;

              return acc;
            },
            0
          );

        setDocumentPrintingState((prev) => {
          if (!prev || !selfAttestedIncomeMeta.data?.id) return prev;
          const newState = [...prev];
          const foundIndex = newState.findIndex(
            (x) => x.reportId == selfAttestedIncomeMeta.data?.id
          );
          if (foundIndex >= 0)
            newState[foundIndex] = {
              status: "ready",
              reportId: selfAttestedIncomeMeta.data.id,
              numDocuments: numDocs
            };

          return newState;
        });
      }
    }
    if (verifiedIncomeMeta.data) {
      const printingState = documentPrintingState?.find(
        (x) => x.reportId == verifiedIncomeMeta.data?.id
      );

      if (printingState?.status !== "ready") {
        const numDocs = Array.isArray(
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          verifiedIncomeMeta.data.report?.document?.assets
        )
          ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            verifiedIncomeMeta.data.report.document?.assets?.filter(
              (file) => file.asset?.filename === "pdf"
            ).length
          : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            verifiedIncomeMeta.data.report.document?.assets?.assets?.filter(
              (file) => file.asset?.filename === "pdf"
            ).length;

        setDocumentPrintingState((prev) => {
          if (!prev || !verifiedIncomeMeta.data?.id) return prev;
          const newState = [...prev];
          const foundIndex = newState.findIndex(
            (x) => x.reportId == verifiedIncomeMeta.data?.id
          );
          if (foundIndex >= 0)
            newState[foundIndex] = {
              status: "ready",
              reportId: verifiedIncomeMeta.data.id,
              numDocuments: numDocs
            };

          return newState;
        });
      }
    }
  }, [
    documentPrintingState,
    selfAttestedIncomeMeta.data,
    verifiedIncomeMeta.data
  ]);

  const verificationIds: number[] = [];
  if (selfAttestedIncomeMeta.data)
    verificationIds.push(selfAttestedIncomeMeta.data.id);
  if (verifiedIncomeMeta.data) verificationIds.push(verifiedIncomeMeta.data.id);

  if (selfAttestedIncomeMeta.isLoading || verifiedIncomeMeta.isLoading)
    return (
      <>
        <ReportHeader title="Income report" reportDate="" verificationId={0} />
        <Loader />
      </>
    );

  if (
    verifiedIncomeMeta.data?.status === VerificationStatus.Failed ||
    selfAttestedIncomeMeta.data?.status === VerificationStatus.Failed ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (verificationTemplate?.verifications?.find(
      (el) => el.name === VerificationNames.VerifiedIncomeVerification
    )
      ? Object.keys(verifiedIncomeMeta.data?.report || {}).length === 0
      : false) ||
    Object.keys(selfAttestedIncomeMeta.data?.report || {}).length === 0
  )
    return <ReportException title="Income report" />;

  return (
    <>
      <div>
        <ReportHeader
          idsWhichMayHaveFiles={
            selfAttestedIncomeMeta.data?.id
              ? [selfAttestedIncomeMeta.data.id]
              : undefined
          }
          title="Income report"
          verificationId={verificationIds}
          reportDate={
            selfAttestedIncomeMeta.data?.completed_at ||
            verifiedIncomeMeta.data?.completed_at ||
            "-"
          }
        />
      </div>

      {Boolean(selfAttestedIncomeMeta.data?.id) ? (
        <PrintReady verificationId={Number(selfAttestedIncomeMeta.data?.id)} />
      ) : null}

      {verifiedIncomeMeta.data?.id ? (
        <PrintReady verificationId={Number(verifiedIncomeMeta.data.id)} />
      ) : null}

      <div className="flex w-full flex-col text-sm">
        <div className="mt-6">
          <VerifiedIncomeSummary
            report={verifiedIncomeMeta.data?.report}
            setShowEditIncomeModal={setShowEditIncomeModal}
          />
          <SelfReportedIncomeSummary
            verifiedReport={verifiedIncomeMeta.data?.report}
            report={selfAttestedIncomeMeta.data?.report}
          />
          {verifiedIncomeMeta.data?.id ? (
            <DocumentWarnings
              report={verifiedIncomeMeta.data.report}
              executeScrollToHA={executeScrollToHA}
              executeScrollToOfferLetter={executeScrollToOfferLetter}
            />
          ) : null}

          <VerifiedIncomeSources report={verifiedIncomeMeta.data?.report} />
          <VerifiedNetEarnings report={verifiedIncomeMeta.data?.report} />

          <SelfReportedIncomeSources
            report={selfAttestedIncomeMeta.data?.report}
            offerLetterScroll={offerLetterScroll}
          />

          <Show
            if={Boolean(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verifiedIncomeMeta.data?.report.housing_assistance_program?.asset
            )}
          >
            <div ref={housingAssistanceScroll}>
              <HousingAssistance
                housing_assistance_program={
                  verifiedIncomeMeta.data?.report.housing_assistance_program
                }
              />
            </div>
          </Show>

          <ProjectedDebtIncomeRatio report={verifiedIncomeMeta.data?.report} />
        </div>
      </div>

      {showEditIncomeModal ? (
        <EditIncomeModal
          opened={showEditIncomeModal}
          onClose={() => setShowEditIncomeModal(false)}
          applicantName={
            applicant.first_name && applicant.last_name
              ? getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                })
              : applicant.email || applicant.phone
          }
          role={
            applicant.role === ApplicantType.Applicant
              ? "Applicant"
              : "Guarantor"
          }
          applicantId={applicant.id}
          applicationId={applicant.application_id}
          report={verifiedIncomeMeta.data?.report}
          refetchVerification={verifiedIncomeMeta.refetch}
          templateRecommendationSettings={templateRecommendationSettings}
        />
      ) : null}
    </>
  );
};
